// src/pages/CreateStoryPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import StoryBookForm from '../components/StoryBookForm';
import LoginModal from '../components/auth/LoginModal';
import { consumeAuthReturnPath, getSafeSessionStorage, peekAuthReturnPath } from '../config/authReturn';
import { getSupportNeedValueFromRouteParam } from '../config/storybookOptions';
import '../styles/create-story.css';

const asArray = (value) => (Array.isArray(value) ? value : []);

const CreateStoryFlowPreview = ({ steps = [], label = '' }) => {
  if (!steps.length) {
    return null;
  }

  return (
    <ol className="create-story-flow-preview" aria-label={label}>
      {steps.map((step, index) => (
        <li key={step.title || index}>
          <span className="create-story-flow-number">{String(index + 1).padStart(2, '0')}</span>
          <span className="create-story-flow-copy">
            <strong>{step.title}</strong>
            <small>{step.text}</small>
          </span>
        </li>
      ))}
    </ol>
  );
};

const CreateStoryPage = () => {
  const { loading, user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const firstName = user?.name?.split(' ')[0] || t('createStoryPage.fallbackName');
  const assurances = asArray(t('createStoryPage.assurances', { returnObjects: true }));
  const flowSteps = asArray(t('createStoryPage.flow.steps', { returnObjects: true }));
  const safeSessionStorage = typeof window !== 'undefined'
    ? getSafeSessionStorage(window)
    : null;
  const savedAuthReturnPath = safeSessionStorage
    ? peekAuthReturnPath(safeSessionStorage)
    : '';
  const initialChildNeed = useMemo(() => {
    const currentNeedParam = new URLSearchParams(location.search).get('need');

    if (currentNeedParam) {
      return getSupportNeedValueFromRouteParam(currentNeedParam);
    }

    if (!savedAuthReturnPath) {
      return '';
    }

    try {
      const savedReturnUrl = new URL(savedAuthReturnPath, window.location.origin);
      return getSupportNeedValueFromRouteParam(savedReturnUrl.searchParams.get('need'));
    } catch {
      return '';
    }
  }, [location.search, savedAuthReturnPath]);

  useEffect(() => {
    if (!user || !safeSessionStorage || !savedAuthReturnPath) {
      return;
    }

    consumeAuthReturnPath(safeSessionStorage);
  }, [safeSessionStorage, savedAuthReturnPath, user]);

  if (loading) {
    return (
      <div className="create-story-page">
        <div className="container">
          <div className="auth-required create-story-loading" role="status" aria-live="polite">
            <div className="spinner" aria-hidden="true"></div>
            <h1>{t('createStoryPage.loadingTitle')}</h1>
            <p>{t('createStoryPage.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="create-story-page">
        <div className="container">
          <div className="auth-required">
            <h1>{t('createStoryPage.signedOutTitle')}</h1>
            <p>{t('createStoryPage.signedOutSubtitle')}</p>
            <CreateStoryFlowPreview
              steps={flowSteps}
              label={t('createStoryPage.flow.label')}
            />
            <button
              className="btn-primary"
              onClick={() => setIsLoginModalOpen(true)}
            >
              {t('createStoryPage.signInCta')}
            </button>
          </div>
        </div>

        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="create-story-page">
      <div className="container">
        <div className="page-header">
          <h1>{t('createStoryPage.title')}</h1>
          <p>{t('createStoryPage.subtitle', { firstName })}</p>
        </div>

        {assurances.length > 0 && (
          <ul className="create-story-assurance" aria-label={t('createStoryPage.assuranceLabel')}>
            {assurances.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        )}

        <CreateStoryFlowPreview
          steps={flowSteps}
          label={t('createStoryPage.flow.label')}
        />

        <StoryBookForm
          initialChildNeed={initialChildNeed}
          onAuthRequired={() => setIsLoginModalOpen(true)}
        />
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
      />
    </div>
  );
};

export default CreateStoryPage;
